import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Switch,
  FormControlLabel,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as AreaIcon,
  LocationOn as LocationIcon,
  Sell as SellIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material';
import Seo from '../components/Seo';
import { formatGBP, getImageUrl, getPropertyCategories, getPropertyTypes, getPropertyLocations, getPropertyListings } from '../services/api';

const propertyFallbackImages = [
  '/Gallery/RoomTypes/4d5b1ac7-9dfb-49a5-beae-765489d5f489.jpg',
  '/Gallery/RoomTypes/467aa065-a980-490f-acb9-1b2c5e98e5d7.jpg',
  '/Gallery/RoomTypes/5f0d61dc-044d-40da-a399-3b687f386694.jpg',
  '/Gallery/RoomTypes/272a244c-aab0-4a9e-8967-5fd34ffc3d48.jpg',
  '/Gallery/RoomTypes/a8305591-83c2-44de-a7a2-c1ef6d9ce0a2.jpg',
  '/Gallery/RoomTypes/b83d4d98-3f6d-4ea1-a731-c1b94db6569b.jpg',
];

const perPageOptions = [10, 20, 50, 100, 200, 500, 1000, 5000];

const PropertyListings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const purposeShortcuts = ['all', 'buy', 'sell', 'rent'];
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState(() => ({
    category: String(searchParams.get('category') || 'all').trim().toLowerCase(),
    propertyType: String(searchParams.get('propertyType') || 'all').trim().toLowerCase(),
    location: String(searchParams.get('location') || 'all').trim().toLowerCase(),
    search: searchParams.get('search') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    featuredOnly: searchParams.get('featuredOnly') === 'true',
    sortBy: String(searchParams.get('sortBy') || 'featured').trim().toLowerCase(),
  }));

  const normalizeValue = (value) => String(value || '').trim().toLowerCase();

  const locationOptions = useMemo(() => {
    const valuesMap = new Map();

    const pushValue = (rawValue, labelOverride) => {
      const normalized = normalizeValue(rawValue);
      if (!normalized || normalized === 'all') return;
      if (!valuesMap.has(normalized)) {
        valuesMap.set(normalized, {
          value: normalized,
          label: labelOverride || String(rawValue || '').trim(),
        });
      }
    };

    locations.forEach((item) => {
      pushValue(item?.name || item?.city || item?.state || item?.county || item?.region);
    });

    listings.forEach((item) => {
      [
        item?.location,
        item?.city,
        item?.state,
        item?.county,
        item?.region,
        item?.postcode,
        item?.postalCode,
        item?.country,
      ].forEach((value) => pushValue(value));
    });

    return Array.from(valuesMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [listings, locations]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (filters.category && filters.category !== 'all') nextParams.set('category', filters.category);
    if (filters.propertyType && filters.propertyType !== 'all') nextParams.set('propertyType', filters.propertyType);
    if (filters.location && filters.location !== 'all') nextParams.set('location', filters.location);
    if (filters.search.trim()) nextParams.set('search', filters.search.trim());
    if (filters.minPrice !== '') nextParams.set('minPrice', filters.minPrice);
    if (filters.maxPrice !== '') nextParams.set('maxPrice', filters.maxPrice);
    if (filters.featuredOnly) nextParams.set('featuredOnly', 'true');
    if (filters.sortBy && filters.sortBy !== 'featured') nextParams.set('sortBy', filters.sortBy);

    const currentSerialized = searchParams.toString();
    const nextSerialized = nextParams.toString();
    if (currentSerialized !== nextSerialized) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const [categoriesRes, typesRes, locationsRes, listingsRes] = await Promise.all([
          getPropertyCategories(),
          getPropertyTypes(),
          getPropertyLocations(),
          getPropertyListings({ page: 1, pageSize: 200 }),
        ]);

        setCategories(categoriesRes?.data || []);
        setTypes(typesRes?.data || []);
        setLocations(locationsRes?.data || []);
        setListings(listingsRes?.data || []);
      } catch (err) {
        console.error('Failed to load property listings:', err);
        setError('Failed to load property listings');
        setCategories([]);
        setTypes([]);
        setLocations([]);
        setListings([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filteredListings = useMemo(() => {
    const filtered = listings.filter((item) => {
      const categoryValue = normalizeValue(item.categoryCode || item.category);
      const typeValue = normalizeValue(item.propertyTypeCode || item.propertyType);
      const locationNeedle = normalizeValue(filters.location);
      const locationFields = [
        item.location,
        item.city,
        item.state,
        item.county,
        item.region,
        item.address,
        item.postcode,
        item.postalCode,
        item.country,
      ]
        .map((value) => normalizeValue(value))
        .filter(Boolean);

      const categoryMatch = filters.category === 'all' || categoryValue === normalizeValue(filters.category);
      const typeMatch = filters.propertyType === 'all' || typeValue === normalizeValue(filters.propertyType);
      const locationMatch = filters.location === 'all' || locationFields.some((value) => value.includes(locationNeedle));
      const minPrice = filters.minPrice === '' ? null : Number(filters.minPrice);
      const maxPrice = filters.maxPrice === '' ? null : Number(filters.maxPrice);
      const priceValue = Number(item.price || 0);
      const minPriceMatch = minPrice === null || Number.isNaN(minPrice) || priceValue >= minPrice;
      const maxPriceMatch = maxPrice === null || Number.isNaN(maxPrice) || priceValue <= maxPrice;
      const featuredMatch = !filters.featuredOnly || Boolean(item.isFeatured);
      const searchTerm = normalizeValue(filters.search);
      const searchMatch = !searchTerm ||
        [
          item.title,
          item.subtitle,
          item.description,
          item.location,
          item.city,
          item.state,
          item.county,
          item.region,
          item.address,
          item.postcode,
          item.postalCode,
          item.country,
          item.propertyType,
          item.category,
        ].some((value) => normalizeValue(value).includes(searchTerm));

      return categoryMatch && typeMatch && locationMatch && minPriceMatch && maxPriceMatch && featuredMatch && searchMatch;
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (filters.sortBy) {
        case 'priceLowHigh':
          return Number(a.price || 0) - Number(b.price || 0);
        case 'priceHighLow':
          return Number(b.price || 0) - Number(a.price || 0);
        case 'titleAsc':
          return String(a.title || '').localeCompare(String(b.title || ''));
        case 'titleDesc':
          return String(b.title || '').localeCompare(String(a.title || ''));
        case 'featured':
        default:
          if (Boolean(a.isFeatured) !== Boolean(b.isFeatured)) {
            return Number(Boolean(b.isFeatured)) - Number(Boolean(a.isFeatured));
          }
          return Number(b.displayOrder || 0) - Number(a.displayOrder || 0);
      }
    });

    return sorted;
  }, [filters, listings]);

  const paginatedListings = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredListings.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredListings]);

  const totalPages = Math.max(1, Math.ceil(filteredListings.length / pageSize));

  const handleFilterChange = (field) => (event) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleFeaturedToggle = (event) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, featuredOnly: event.target.checked }));
  };

  const handlePurposeShortcut = (value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, category: value }));
  };

  const resetFilters = () => {
    setCurrentPage(1);
    setFilters({
      category: 'all',
      propertyType: 'all',
      location: 'all',
      search: '',
      minPrice: '',
      maxPrice: '',
      featuredOnly: false,
      sortBy: 'featured',
    });
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event) => {
    setCurrentPage(1);
    setPageSize(Number(event.target.value));
  };

  const getListingImage = (listing) => {
    const primary = listing?.primaryImagePath || listing?.images?.find((img) => img.isPrimary)?.imagePath || listing?.images?.[0]?.imagePath || '';
    const normalizedPrimary = String(primary || '').trim();

    if (normalizedPrimary) {
      if (normalizedPrimary.startsWith('/Gallery/')) {
        return getImageUrl(normalizedPrimary);
      }

      if (normalizedPrimary.startsWith('http://') || normalizedPrimary.startsWith('https://')) {
        return getImageUrl(normalizedPrimary);
      }

      if (normalizedPrimary.includes('/Gallery/')) {
        return getImageUrl(normalizedPrimary.substring(normalizedPrimary.indexOf('/Gallery/')));
      }

      return getImageUrl(normalizedPrimary, 'property');
    }

    const fallbackImage = propertyFallbackImages[Number(listing?.id || 0) % propertyFallbackImages.length];
    return getImageUrl(fallbackImage);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Seo
        title="Property Listings"
        description="Explore buy, sell and rent property listings including homes, villas, offices and plots across multiple locations."
        keywords="property listings, buy property, sell property, rent house, villa, office, plot"
        author="Grosvenor Plymouth"
      />

      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #0f172a 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Chip icon={<SellIcon />} label="New Module" sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.75rem', sm: '2.2rem', md: '2.5rem' } }}>
              Buy, Sell & Rent Properties
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 900, opacity: 0.9, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
              Discover houses, villas, offices, plots and more across multiple locations with a scalable property module foundation.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack direction="row" spacing={1.5} sx={{ mb: { xs: 2.5, md: 3 }, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }} useFlexGap>
          {purposeShortcuts.map((purpose) => (
            <Button
              key={purpose}
              variant={filters.category === purpose ? 'contained' : 'outlined'}
              onClick={() => handlePurposeShortcut(purpose)}
              sx={{ textTransform: 'capitalize', borderRadius: 999, minWidth: { xs: 'calc(50% - 6px)', sm: 'auto' }, px: { xs: 1.75, sm: 2 } }}
            >
              {purpose === 'all' ? 'All Properties' : purpose}
            </Button>
          ))}
        </Stack>

        <Card sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: { xs: 3, md: 4 }, borderRadius: { xs: 3, md: 4 }, boxShadow: '0 14px 35px rgba(15, 23, 42, 0.08)', border: '1px solid rgba(148, 163, 184, 0.18)' }}>
          <Grid
            container
            spacing={{ xs: 2, sm: 2, lg: 1.5 }}
            alignItems="stretch"
          >
            <Grid item xs={12} sm={6} lg={2.2} sx={{ minWidth: { lg: 170 } }}>
              <TextField
                fullWidth
                label="Search property"
                value={filters.search}
                onChange={handleFilterChange('search')}
                placeholder="Search by title or location"
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: 18, md: 20 } }} /> }}
                size="small"
                sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, '& .MuiOutlinedInput-root': { borderRadius: 2.5, backgroundColor: '#fff' }, '& .MuiInputLabel-root': { fontSize: { xs: '0.9rem', md: '1rem' } } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={1.4} sx={{ minWidth: { lg: 135 } }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>Purpose</InputLabel>
                <Select value={filters.category} label="Purpose" onChange={handleFilterChange('category')} sx={{ borderRadius: 2.5, backgroundColor: '#fff' }}>
                  <MenuItem value="all">All Purposes</MenuItem>
                  {categories.map((item) => (
                    <MenuItem key={item.id} value={String(item.code || item.name || '').toLowerCase()}>{item.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} lg={1.4} sx={{ minWidth: { lg: 135 } }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>Property Type</InputLabel>
                <Select value={filters.propertyType} label="Property Type" onChange={handleFilterChange('propertyType')} sx={{ borderRadius: 2.5, backgroundColor: '#fff' }}>
                  <MenuItem value="all">All Types</MenuItem>
                  {types.map((item) => (
                    <MenuItem key={item.id} value={String(item.code || item.name || '').toLowerCase()}>{item.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} lg={1.4} sx={{ minWidth: { lg: 135 } }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>Location</InputLabel>
                <Select value={filters.location} label="Location" onChange={handleFilterChange('location')} sx={{ borderRadius: 2.5, backgroundColor: '#fff' }}>
                  <MenuItem value="all">All Locations</MenuItem>
                  {locationOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} lg={1.3} sx={{ minWidth: { lg: 130 } }}>
              <TextField
                fullWidth
                label="Min Price"
                type="number"
                value={filters.minPrice}
                onChange={handleFilterChange('minPrice')}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, backgroundColor: '#fff' }, '& .MuiInputLabel-root': { fontSize: { xs: '0.9rem', md: '1rem' } } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={1.3} sx={{ minWidth: { lg: 130 } }}>
              <TextField
                fullWidth
                label="Max Price"
                type="number"
                value={filters.maxPrice}
                onChange={handleFilterChange('maxPrice')}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, backgroundColor: '#fff' }, '& .MuiInputLabel-root': { fontSize: { xs: '0.9rem', md: '1rem' } } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={1.6} sx={{ minWidth: { lg: 150 } }}>
              <Stack direction={{ xs: 'row', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1} sx={{ height: '100%', minHeight: 40, borderRadius: 2.5, px: { xs: 1.5, sm: 1 }, py: { xs: 0.5, sm: 0 }, backgroundColor: { xs: '#f8fafc', sm: 'transparent' } }}>
                <FormControlLabel
                  control={<Switch checked={filters.featuredOnly} onChange={handleFeaturedToggle} size="small" />}
                  label="Featured"
                  sx={{ mr: 0, ml: 0, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }}
                />
                <Button onClick={resetFilters} size="small" variant="text" sx={{ minWidth: 'auto', px: 1.5, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>Reset</Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} lg={1.6} sx={{ minWidth: { lg: 150 } }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>Sort By</InputLabel>
                <Select value={filters.sortBy} label="Sort By" onChange={handleFilterChange('sortBy')} sx={{ borderRadius: 2.5, backgroundColor: '#fff' }}>
                  <MenuItem value="featured">Featured First</MenuItem>
                  <MenuItem value="priceLowHigh">Price: Low to High</MenuItem>
                  <MenuItem value="priceLowHigh">Price: High to Low</MenuItem>
                  <MenuItem value="titleAsc">Title: A to Z</MenuItem>
                  <MenuItem value="titleDesc">Title: Z to A</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={56} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="error" variant="h6">{error}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2.125rem' }, textAlign: { xs: 'center', sm: 'left' } }}>
                Available Listings
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent={{ xs: 'center', sm: 'flex-end' }} flexWrap="wrap" useFlexGap sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Stack direction="row" spacing={0.5} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
                  <Button
                    size="small"
                    variant={viewMode === 'grid' ? 'contained' : 'text'}
                    onClick={() => setViewMode('grid')}
                    sx={{ minWidth: 40, px: 1 }}
                  >
                    <GridViewIcon fontSize="small" />
                  </Button>
                  <Button
                    size="small"
                    variant={viewMode === 'list' ? 'contained' : 'text'}
                    onClick={() => setViewMode('list')}
                    sx={{ minWidth: 40, px: 1 }}
                  >
                    <ListViewIcon fontSize="small" />
                  </Button>
                </Stack>
                <FormControl size="small" sx={{ minWidth: 130, width: { xs: '100%', sm: 'auto' } }}>
                  <InputLabel>Per Page</InputLabel>
                  <Select value={pageSize} label="Per Page" onChange={handlePageSizeChange}>
                    {perPageOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Chip label={`${filteredListings.length} properties`} color="primary" sx={{ justifyContent: 'center' }} />
              </Stack>
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>
              {paginatedListings.map((listing) => (
                <Grid item xs={12} sm={6} md={4} key={listing.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: viewMode === 'list' ? { xs: 'column', sm: 'row' } : 'column', borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.15)' } }}>
                    <CardMedia
                      component="img"
                      image={getListingImage(listing)}
                      alt={listing.title}
                      sx={{ objectFit: 'cover', height: viewMode === 'list' ? { xs: 220, sm: '100%' } : 240, width: viewMode === 'list' ? { xs: '100%', sm: 300 } : '100%', flexShrink: 0 }}
                      onError={(event) => {
                        const fallbackImage = propertyFallbackImages[Number(listing?.id || 0) % propertyFallbackImages.length];
                        if (event.currentTarget.src !== getImageUrl(fallbackImage)) {
                          event.currentTarget.src = getImageUrl(fallbackImage);
                        }
                      }}
                    />
                    <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip size="small" label={listing.category || 'Property'} color="primary" sx={{ fontSize: '0.75rem', height: 24 }} />
                        <Chip size="small" label={listing.propertyType || 'Type'} variant="outlined" sx={{ fontSize: '0.75rem', height: 24 }} />
                        {listing.isFeatured ? <Chip size="small" label="Featured" color="warning" sx={{ fontSize: '0.75rem', height: 24 }} /> : null}
                      </Stack>

                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' }, lineHeight: 1.3, minHeight: { xs: 'auto', md: 50 } }}>
                        {listing.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 38, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', fontSize: '0.875rem' }}>
                        {listing.subtitle || listing.description || 'Premium property listing'}
                      </Typography>

                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5, color: 'text.secondary' }}>
                        <LocationIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{listing.location}{listing.city ? `, ${listing.city}` : ''}</Typography>
                      </Stack>

                      <Stack direction="row" spacing={1.5} sx={{ mb: 2, color: 'text.secondary', flexWrap: 'wrap' }}>
                        {listing.bedrooms ? <Stack direction="row" spacing={0.5} alignItems="center"><BedIcon sx={{ fontSize: 18 }} /><Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{listing.bedrooms} Beds</Typography></Stack> : null}
                        {listing.bathrooms ? <Stack direction="row" spacing={0.5} alignItems="center"><BathIcon sx={{ fontSize: 18 }} /><Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{listing.bathrooms} Baths</Typography></Stack> : null}
                        {listing.areaSize ? <Stack direction="row" spacing={0.5} alignItems="center"><AreaIcon sx={{ fontSize: 18 }} /><Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{listing.areaSize} {listing.areaUnit || 'sq ft'}</Typography></Stack> : null}
                      </Stack>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 'auto', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>Starting from</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '1.35rem' }}>
                            {formatGBP(listing.price || 0)}
                          </Typography>
                        </Box>
                        <Button component={Link} to={`/properties/${listing.id}`} variant="contained" size="medium" sx={{ width: { xs: '100%', sm: 'auto' }, alignSelf: 'flex-end', px: 2.5, py: 1 }}>View Details</Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredListings.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, overflowX: 'auto', px: 1 }}>
                <Pagination
                  page={currentPage}
                  count={totalPages}
                  color="primary"
                  onChange={handlePageChange}
                  shape="rounded"
                />
              </Box>
            ) : null}

            {!filteredListings.length ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography variant="h6" color="text.secondary">No property listings found yet.</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  The module foundation is now ready. Next we can add admin listing management and seed initial property data.
                </Typography>
              </Box>
            ) : null}
          </>
        )}
      </Container>
    </Box>
  );
};

export default PropertyListings;
